import { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { CheckCircle, XCircle, Loader2, Server, Database, Zap, ArrowLeft } from 'lucide-react';
import { testTurfAIConnection, testDocumentTypes, testCreateCollection, testWorkflowResolution } from '../services/turfai/testConnection';

interface ConnectionStatus {
  auth: boolean | null;
  dms: boolean | null;
  workflow: boolean | null;
}

interface TurfAIConnectionTestProps {
  onBack?: () => void;
}

export function TurfAIConnectionTest({ onBack }: TurfAIConnectionTestProps) {
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    auth: null,
    dms: null,
    workflow: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [collectionId, setCollectionId] = useState<string | null>(null);

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setError(null);
    setConnectionStatus({ auth: null, dms: null, workflow: null });

    try {
      const result = await testTurfAIConnection();
      setConnectionStatus(result.results);
      
      if (!result.success && result.error) {
        setError(result.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleTestDocumentTypes = async () => {
    try {
      await testDocumentTypes();
      // Check console for output
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch document types');
    }
  };

  const handleTestWorkflowResolution = async () => {
    try {
      await testWorkflowResolution();
      // Check console for output
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test workflow resolution');
    }
  };

  const handleTestCreateCollection = async () => {
    try {
      const id = await testCreateCollection();
      if (id) {
        setCollectionId(id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create collection');
    }
  };

  const getStatusIcon = (status: boolean | null) => {
    if (status === null) return <div className="w-5 h-5" />;
    return status ? (
      <CheckCircle className="w-5 h-5 text-green-500" />
    ) : (
      <XCircle className="w-5 h-5 text-red-500" />
    );
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <CardTitle>TurfAI Connection Test</CardTitle>
        </div>
        <CardDescription>
          Test the connection to TurfAI services including authentication, DMS, and workflow services.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection Test Button */}
        <div className="flex flex-col gap-4">
          <Button 
            onClick={handleTestConnection}
            disabled={isTestingConnection}
            className="w-full"
          >
            {isTestingConnection ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing Connection...
              </>
            ) : (
              <>
                <Server className="mr-2 h-4 w-4" />
                Test TurfAI Connection
              </>
            )}
          </Button>

          {/* Additional Test Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button 
              onClick={handleTestDocumentTypes}
              variant="outline"
              size="sm"
              disabled={!connectionStatus.auth}
            >
              Test Document Types
            </Button>
            <Button 
              onClick={handleTestCreateCollection}
              variant="outline"
              size="sm"
              disabled={!connectionStatus.auth}
            >
              Test Create Collection
            </Button>
            <Button 
              onClick={handleTestWorkflowResolution}
              variant="outline"
              size="sm"
              disabled={!connectionStatus.auth}
              className="col-span-2"
            >
              Test Workflow Resolution
            </Button>
          </div>
        </div>

        {/* Connection Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">Authentication</span>
            </div>
            {getStatusIcon(connectionStatus.auth)}
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">Document Management (DMS)</span>
            </div>
            {getStatusIcon(connectionStatus.dms)}
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">Workflow Service</span>
            </div>
            {getStatusIcon(connectionStatus.workflow)}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Connection Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success Message */}
        {connectionStatus.auth && connectionStatus.dms && connectionStatus.workflow && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-900">Connection Successful</AlertTitle>
            <AlertDescription className="text-green-800">
              All TurfAI services are connected and ready to use.
            </AlertDescription>
          </Alert>
        )}

        {/* Collection ID Display */}
        {collectionId && (
          <Alert>
            <AlertTitle>Test Collection Created</AlertTitle>
            <AlertDescription>
              Collection ID: <code className="text-sm bg-muted px-1 py-0.5 rounded">{collectionId}</code>
            </AlertDescription>
          </Alert>
        )}

        {/* Instructions */}
        <div className="text-sm text-muted-foreground space-y-1">
          <p>• Check the browser console for detailed logs</p>
          <p>• Ensure you have the correct API credentials in your .env file</p>
          <p>• The test uses the credentials: turfai@openturf.in</p>
        </div>
      </CardContent>
    </Card>
  );
}